import videojs from 'video.js';
import Quality from './quality.js';

const USER_QUALITY_STORAGE_KEY = 'wc_user_quality';
const AUTO_QUALITY_STORAGE_KEY = 'wc_auto_quality';

/**
 * setup the common parts of an audio, video, or text track
 *
 * @param {String} type The type of track we are dealing with audio|video|text
 * @param {Object=} options Object of option names and values
 * @extends EventTarget
 */
class QualityList extends videojs.EventTarget {
  constructor(options) {
    super();
    this.list = [];
    this.restricted_qualities = options.restricted_qualities || [];
    this.unavailable_qualities = options.unavailable_qualities || [];
    this.clear();
  }

  addQuality(quality) {
    this.list.push(quality);

    this._sort();
    if (this.list.length == 1) {
      this.selectQuality(this.list[0]);
    }
    this.trigger('change');
  }

  clear() {
    this.list = [];
    // auto quality
    this.addQuality(new Quality());
    this.trigger('change');
  }

  removeQuality(quality) {
    let index = this.list.indexOf(quality);
    if (~index) {
      let q = this.list.splice(index, 1)[0];
      if (q.selected && this.list.length > 0) {
        this.selectQuality(this.list[0]);
      }
      q.selected = false;
      this.trigger('change');
    }
  }

  selectQuality(quality, user_click = false, remember_quality = false) {
    this._markSelected(quality);
    if (remember_quality) {
      this._saveUserPreferredQuality(quality);
    }
    this.trigger('change_selected');
    if (user_click) {
      this.trigger('user_quality_changed');
    }
  }

  forceQuality(quality) {
    this._markSelected(quality);
    this.trigger('change_forced');
  }

  getLastAutoQuality() {
    if (!this._hasLocalStorage()) return;
    let quality_label = localStorage.getItem(AUTO_QUALITY_STORAGE_KEY);
    if (!quality_label) {
      return;
    }
    return this._findQualityByLabel(quality_label);
  }

  getUserPreferredQuality() {
    if (!this._hasLocalStorage()) return;

    let qualityInfo;
    try {
      const storedData = localStorage.getItem(USER_QUALITY_STORAGE_KEY);
      if (!storedData) {
        return this.getAutoQuality();
      }
      qualityInfo = JSON.parse(storedData);
    } catch (error) {
      // Если произошла ошибка при парсинге, возвращаем "Авто"
      return this.getAutoQuality();
    }

    if (!qualityInfo) {
      return this.getAutoQuality();
    }

    const preferredQuality = this._findClosestQuality(qualityInfo.height);

    // Если качество ограничено или не найдено, возвращаем "Авто"
    if (!preferredQuality || this._qualityRestricted(preferredQuality)) {
      return this.getAutoQuality();
    }

    return preferredQuality;
  }

  getAutoQuality() {
    return this.list.find(quality => quality.auto);
  }

  saveAutoQuality(quality) {
    if (!this._canSaveQuality(quality)) {
      return;
    }
    localStorage.setItem(AUTO_QUALITY_STORAGE_KEY, quality.label);
  }

  clearAutoQuality() {
    if (!this._hasLocalStorage()) return;
    localStorage.removeItem(USER_QUALITY_STORAGE_KEY);
  }

  get selectedQuality() {
    return this.list.find(quality => quality.selected);
  }

  get isAuto() {
    return !!this.list.find(quality => quality.auto && quality.selected);
  }

  [Symbol.iterator]() {
    return this.list[Symbol.iterator]();
  }

  _markSelected(quality) {
    for (let q of this.list) {
      q.selected = q == quality;
    }
  }

  _sort() {
    this.list = this.list.sort(function(a, b) {
      if (a.bitrate && b.bitrate) {
        if (a.bitrate < b.bitrate) return 1;
        if (a.bitrate > b.bitrate) return -1;
        return 0;
      } else if (isNaN(a.bitrate) && b.bitrate || a.bitrate && isNaN(b.bitrate)) {
        if (!a.bitrate) return 1;
        if (!b.bitrate) return -1;
      } else {
        let a_num = Number.parseInt(a.label);
        let b_num = Number.parseInt(b.label);
        if (!isNaN(a_num) && !isNaN(b_num)) {
          if (a_num < b_num) return 1;
          if (a_num > b_num) return -1;
          return 0;
        } else {
          if (a.label == 'Auto') return 1;
          if (b.label == 'Auto') return -1;
          if (a.label < b.label) return 1;
          if (a.label > b.label) return -1;
          return 0;
        }
      }
    });
  }

  _saveUserPreferredQuality(quality) {
    if (!this._canSaveQuality(quality)) {
      return;
    }
    const qualityInfo = JSON.stringify({ label: quality.label, height: quality.height });
    localStorage.setItem(USER_QUALITY_STORAGE_KEY, qualityInfo);
  }

  _canSaveQuality(quality) {
    if (!this._hasLocalStorage()) return false;
    if (!quality || !quality.label) {
      return false;
    }
    return true;
  }

  _hasLocalStorage() {
    try {
      window.localStorage.setItem('checkLocalStorage', 'checkLocalStorage');
      window.localStorage.removeItem('checkLocalStorage');
      return true;
    } catch (e) {
      return false;
    }
  }

  _findQualityByLabel(quality_label) {
    const quality = this.list.find(quality => quality.label == quality_label);
    if (!quality || this._qualityRestricted(quality)) {
      return;
    }
    return quality;
  }

  _findClosestQuality(targetHeight) {
    const threshold = targetHeight * 0.3;

    const closestQuality = this.list.reduce((closest, current) => {
      return Math.abs(current.height - targetHeight) < Math.abs(closest.height - targetHeight) ? current : closest;
    });

    if (closestQuality.height <= targetHeight + threshold && closestQuality.height >= targetHeight - threshold) {
      return closestQuality;
    }

    return null;
  }

  _qualityRestricted(quality) {
    const isQualityRestricted = this.restricted_qualities.includes(quality.name);
    const isQualityUnavailable = this.unavailable_qualities.includes(quality.name);
    return isQualityUnavailable || isQualityRestricted;
  }
}

export default QualityList;
